import { cloneElement, isValidElement, useId, type ReactElement, type ReactNode } from 'react';
import { cn } from '../../lib/utils.js';
import { HelpTip } from './help-tip.js';
import type { HelpKey } from '../../help/helpText.js';

interface Props {
  label: string;
  /** Help tip key (CLAUDE.md "Help tips everywhere": every form field has one). */
  help: HelpKey;
  hint?: ReactNode;
  error?: string | null;
  required?: boolean;
  className?: string;
  /** A single input-like element; it receives id / aria-describedby / aria-invalid. */
  children: ReactElement;
}

export function FormField({ label, help, hint, error, required, className, children }: Props) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errId = `${id}-err`;
  const describedBy = [hint ? hintId : null, error ? errId : null].filter(Boolean).join(' ') || undefined;
  const child = isValidElement(children)
    ? cloneElement(children as ReactElement<Record<string, unknown>>, { id, 'aria-describedby': describedBy, 'aria-invalid': error ? true : undefined })
    : children;
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <div className="flex items-center gap-1.5">
        <label htmlFor={id} className="text-sm font-medium">
          {label}{required && <span className="ml-0.5 text-danger" aria-hidden>*</span>}
        </label>
        <HelpTip k={help} />
      </div>
      {child}
      {hint && !error && <p id={hintId} className="text-xs text-muted-foreground">{hint}</p>}
      {error && <p id={errId} className="text-xs font-medium text-danger">{error}</p>}
    </div>
  );
}
